/**
 * Máscaras de digitação do fluxo de adesão (at-57, C0 da frente C).
 *
 * Fonte ÚNICA das máscaras que estavam **copiadas** nas telas — medido em
 * 09/09/2026: `formatCep` e a lista de UFs apareciam **6 vezes** cada (as 3 telas
 * de associado e as 3 de carro) e `formatRg`, `formatCelular`,
 * `formatTelefoneFixo` e `formatDDD`, 3 vezes cada. Convenção da at-24: regra
 * repetida em duas telas vira `utils/`.
 *
 * ⚠️ Estas funções são cópia **verbatim** das que já existiam — conferido que as
 * 6 versões eram idênticas entre si (só variavam as aspas, e a lista de UFs uma
 * vírgula final). A C0 não muda comportamento nenhum: extrai e aponta. Qualquer
 * mudança de máscara é das branches seguintes, com decisão registrada.
 *
 * 📌 `formatCpfCnpj` NÃO mora aqui: já existe em `utils/formartCPF.ts` (sic, o
 * nome tem typo), importado por 12 arquivos. As 3 cópias locais das telas de
 * associado eram byte a byte iguais a ele — passam a importar de lá. Duas fontes
 * para a mesma função é o que este arquivo existe para acabar.
 */

/** `00000-000`, no máximo 9 caracteres. */
export const formatCep = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');

  return cleaned.replace(/^(\d{5})(\d)/, '$1-$2').slice(0, 9);
};

/** Tamanho máximo do RG digitado. Cabe "MG-12.345.678" e o padrão SP com folga. */
export const RG_MAX = 20;

/**
 * RG **alfanumérico livre** — decisão **D5** de Victor (09/09/2026), executada
 * na at-58 (C1).
 *
 * Deixou de ser máscara e passou a ser **saneamento**: preserva letras, dígitos e
 * os separadores que as pessoas de fato digitam (`.`, `-`, `/`, espaço), descarta
 * o resto, deixa as letras em MAIÚSCULA e corta em {@link RG_MAX}.
 *
 * ⚠️ A versão anterior (`00.000.000-0`) fazia `replace(/\D/g,'')` e **assumia RG
 * de 9 dígitos no padrão SP**: RG de outros estados (7 ou 8 dígitos, muitos com
 * letra de órgão emissor) não cabia — quem tinha "MG-1.234.567" via as letras
 * desaparecerem enquanto digitava.
 *
 * 🔴 **MAIÚSCULA é normalização deliberada, e o motivo é o banco:** `rg` é
 * `@unique` em `Associado`, então "mg123" e "MG123" seriam **dois RGs
 * diferentes** para o índice — o banco não acusaria a duplicata. Uniformizar a
 * caixa fecha metade dessa porta (a outra metade — pontuação — é tratada no
 * envio; ver `views/documento`).
 *
 * ⚠️ **Não normaliza pontuação aqui, de propósito.** O campo guarda o que o
 * consultor digitou; quem uniformiza para o banco é o passo do cadastro, que
 * sempre fez isso (era `replace(/\D/g,'')`). Fazer as duas coisas no mesmo lugar
 * impediria o consultor de ver o RG na forma do documento.
 */
export const formatRg = (value: string): string =>
  value
    .replace(/[^0-9A-Za-z./\- ]/g, '')
    .toUpperCase()
    .slice(0, RG_MAX);

/**
 * `00000-0000`, no máximo 10 caracteres.
 *
 * 📌 O hífen só entra quando há **exatamente 9 dígitos** (o regex é ancorado em
 * `$`), então durante a digitação o campo mostra os dígitos crus. É o
 * comportamento atual — preservado de propósito.
 */
export const formatCelular = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');

  return cleaned.replace(/^(\d{5})(\d{4})$/, '$1-$2').slice(0, 10);
};

/** `0000-0000`, no máximo 9 caracteres. */
export const formatTelefoneFixo = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');

  return cleaned.replace(/^(\d{4})(\d)/, '$1-$2').slice(0, 9);
};

/**
 * RG como ele vai ao backend — tira a pontuação e **preserva as letras**.
 *
 * 🔴 Existe por causa de um efeito colateral da decisão **D5** que não estava
 * previsto no plano. A etapa 3 (`views/documento`) sempre enviou o RG com
 * `replace(/\D/g, '')`, ou seja, **só os dígitos**; enquanto a máscara também era
 * só de dígitos, isso era inócuo. Com o RG alfanumérico, aquele `replace`
 * **apagaria as letras em silêncio**: o consultor digitaria "MG-1.234.567", veria
 * o valor certo na tela, e o banco receberia "1234567".
 *
 * 📌 Para RG numérico o resultado é **idêntico ao de antes** ("12.345.678-9" →
 * "123456789"), então o acervo já gravado e os novos cadastros seguem na mesma
 * forma — o que importa porque `Associado.rg` é `@unique` e formas diferentes do
 * mesmo RG não colidiriam no índice.
 *
 * ⚠️ Usado pelos **dois** caminhos de cadastro (`documento.tsx` e
 * `documentoEdit.tsx`). Divergir entre eles gravaria o mesmo RG de duas formas.
 */
export const rgParaEnvio = (valor: string): string =>
  (valor || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();

/**
 * Telefone **com DDD num campo só** — `(11) 99999-9999` (celular, 11 dígitos) ou
 * `(11) 4002-8922` (fixo, 10). Introduzida na at-58 (C1).
 *
 * A tela antiga tinha **quatro** campos de telefone (DDD e número, para celular e
 * para fixo), cada um com a sua máscara e o seu `maxLength`. A spec 03 §3.3
 * autoriza expressamente unificar — *"você pode unificar DDD+número num campo só
 * `(00) 00000-0000` desde que grave os dois separados (`dddCelular`,
 * `celular`)"* — e o protótipo aprovado (D7) faz assim.
 *
 * 🔴 **O estado continua guardando `ddd` e `número` SEPARADOS.** Quem junta e
 * separa são {@link juntarDddNumero} e {@link separarDddNumero}; o campo unificado
 * é só apresentação. As chaves do slice (`dddCelular`, `celular`) são contrato da
 * spec 03 §9.1 — o payload do cadastro e os rascunhos já gravados leem por elas.
 */
export const formatTelefone = (value: string): string => {
  const digitos = value.replace(/\D/g, '').slice(0, 11);

  if (digitos.length <= 2) return digitos.replace(/^(\d{1,2})/, '($1');
  // Enquanto não há dígito suficiente para saber se é fixo (10) ou celular (11),
  // o hífen fica de fora: entrar e sair conforme o tamanho faz o cursor pular.
  if (digitos.length <= 6) return `(${digitos.slice(0, 2)}) ${digitos.slice(2)}`;

  const corte = digitos.length > 10 ? 7 : 6;

  return `(${digitos.slice(0, 2)}) ${digitos.slice(2, corte)}-${digitos.slice(corte)}`;
};

/** Junta o que está separado no estado num valor para o campo unificado. */
export const juntarDddNumero = (ddd?: string | null, numero?: string | null): string => {
  const juntos = `${ddd ?? ''}${numero ?? ''}`.replace(/\D/g, '');

  return juntos ? formatTelefone(juntos) : '';
};

/**
 * Separa o campo unificado de volta em `{ ddd, numero }`, como o estado guarda.
 *
 * ⚠️ Telefone **incompleto devolve os dois vazios**: gravar um DDD sem número
 * (ou o contrário) deixaria o cadastro com meio contato, e o backend normaliza os
 * dois por dígitos sem checar se fazem par. Menos de 10 dígitos não é telefone.
 */
export const separarDddNumero = (valor: string): { ddd: string; numero: string } => {
  const digitos = (valor || '').replace(/\D/g, '');

  if (digitos.length < 10) return { ddd: '', numero: '' };

  return { ddd: digitos.slice(0, 2), numero: digitos.slice(2, 11) };
};

/**
 * Placa: MAIÚSCULA, só letras e dígitos, no máximo 7 caracteres (at-62, D1).
 *
 * Extraída porque estava em **dois** formulários — `views/cotacao` (como
 * `formatPlaca` local) e `components/carro/FormularioVeiculo` (embutida em
 * `definirPlaca`) —, com o mesmo corpo. Convenção da at-24.
 *
 * ⚠️ **Não valida o formato**: só sanea o que foi digitado. Quem diz se a placa
 * é uma placa é {@link placaValida}.
 */
export const formatPlaca = (value: string): string =>
  (value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 7);

/**
 * Placa brasileira COMPLETA: Mercosul (`AAA1A23`) ou antiga (`AAA1234`).
 *
 * ⚠️ Só faz sentido com 7 caracteres — placa em digitação é sempre "inválida"
 * por estar incompleta. Os formulários a usam para **recusar a 7ª tecla** quando
 * ela não fecharia um dos dois formatos, e não para marcar erro no meio da
 * digitação.
 */
export const placaValida = (placa: string): boolean =>
  /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(placa) || /^[A-Z]{3}[0-9]{4}$/.test(placa);

/** `(00)`, no máximo 4 caracteres. */
export const formatDDD = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');

  return cleaned.replace(/^(\d{2})/, '($1)').slice(0, 4);
};

/**
 * As 27 UFs, na ordem em que as telas já as listavam (não é alfabética pura —
 * AM vem depois de AP). Mantida assim para o `<select>` não reordenar.
 */
export const UFS: readonly string[] = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
  'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

/**
 * Código FIPE **fictício** do modelo no SGA (at-46; extraído do modal na at-69).
 *
 * Aceita "0000001" e devolve "000000-1"; mantém o que já veio com hífen. O
 * formato é o que o SGA exibe — o TI cadastra o modelo na tela de lá e cola aqui
 * o código gerado.
 */
export const formatCodigoFipe = (value: string): string => {
  const digitos = value.replace(/\D/g, '').slice(0, 7);

  return digitos.length > 6 ? `${digitos.slice(0, 6)}-${digitos.slice(6)}` : digitos;
};

/**
 * ⚠️ **Vazio é VÁLIDO**, e não é descuido: significa "o modelo ainda não foi
 * cadastrado no SGA". A adesão fecha no SAAC de qualquer forma; o que fica
 * pendente é só o envio ao SGA (at-46).
 */
export const codigoFipeValido = (valor: string): boolean => {
  const limpo = valor.trim();

  return limpo === '' || /^\d{6}-\d$/.test(limpo);
};
